/**
 * Utilitaires pour la gestion des logos de sociétés
 */

import env from '@/config/env';

const API_URL = env.PUBLIC_API_URL;
const AWS_S3_FILES_URL = env.AWS_S3_FILES_URL;

/**
 * Construit l'URL de téléchargement d'un document via l'API
 * @param s3Key Clé S3 du document (ex: "societies/uuid/logo/xxx.png")
 * @returns URL de téléchargement via l'API
 */
export const buildDocumentDownloadUrl = (s3Key: string): string => {
  return `${API_URL}/documents/download?path=${encodeURIComponent(s3Key)}`;
};

export type SocietyDocumentKey =
  | 'logo'
  | 'kbis'
  | 'rib'
  | 'managementMandate'
  | 'warrantyLeaflet'
  | 'sepaMandate';

/**
 * Résout l'URL de téléchargement d'un document société en tenant compte du
 * format (V2 clé S3 "societies/..." ou nom legacy).
 *
 * Conventions legacy (cf. outil-gestion-prescripteur) :
 *   - Logo              → S3 public: {AWS_S3_FILES_URL}/logos/{code}/{filename}
 *   - kbis/rib/mandates → SFTP dans le sous-dossier "prescripteur/"
 */
export const resolveSocietyDocumentUrl = (
  society: { code: string },
  docKey: SocietyDocumentKey,
  rawValue: string | null | undefined,
): string | null => {
  if (!rawValue) return null;
  if (rawValue.trim().startsWith('{')) return null;
  if (rawValue.startsWith('http')) return rawValue;
  if (rawValue.startsWith('societies/')) return buildDocumentDownloadUrl(rawValue);
  if (docKey === 'logo') {
    return `${AWS_S3_FILES_URL}/logos/${society.code}/${rawValue}`;
  }
  if (rawValue.includes('/')) return buildDocumentDownloadUrl(rawValue);
  return buildDocumentDownloadUrl(`prescripteur/${rawValue}`);
};

/**
 * Construit l'URL complète du logo V1 (legacy Symfony)
 * Pattern: {AWS_S3_FILES_URL}/logos/{societyCode}/{logoFilename}
 *
 * @param code Code de la société
 * @param logoFilename Nom du fichier logo (ex: "logo.png")
 * @returns URL complète du logo
 */
export const buildLogoUrl = (code: string, logoFilename: string): string => {
  return `${AWS_S3_FILES_URL}/logos/${code}/${logoFilename}`;
};

/**
 * Résout l'URL du logo d'une société en priorisant V2 puis V1
 *
 * Logique:
 * 1. Si config.brandingLogoUrl existe (V2) → utiliser
 * 2. Sinon, si logo existe:
 *    - Si c'est une S3 key (contient "/") → utiliser endpoint API de téléchargement
 *    - Sinon (V1 legacy) → construire URL S3 directe
 * 3. Sinon → null
 *
 * @param society Société avec logo et config
 * @returns URL du logo ou null
 */
export const resolveLogoUrl = (society: {
  code: string;
  logo?: string | null;
  config?: { brandingLogoUrl?: string | null } | null;
}): string | null => {
  // Priorité 1: Logo V2 (URL complète dans config)
  if (society.config?.brandingLogoUrl) {
    return society.config.brandingLogoUrl;
  }

  // Priorité 2: Logo stocké dans PRO_SOCIETY.LOGO
  if (society.logo) {
    // Si c'est une S3 key (V2) → utiliser l'endpoint API
    if (society.logo.includes('/')) {
      return buildDocumentDownloadUrl(society.logo);
    }
    // Sinon, c'est un filename V1 legacy → construire l'URL S3 directe
    return buildLogoUrl(society.code, society.logo);
  }

  // Pas de logo
  return null;
};
